import React from 'react';
import { useAuth } from '../contexts/AuthContext';

interface PermissionGuardProps {
  permission: string;
  projectId?: number;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAll?: boolean; // If multiple permissions, require all instead of any
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  projectId,
  children,
  fallback = null,
  requireAll = false
}) => {
  const { hasPermission, hasProjectAccess, getUserProjectPermissions, user } = useAuth();

  // If user is super admin, allow everything
  if (user?.allPermissions?.includes('*') || user?.allPermissions?.includes('superadmin')) {
    return <>{children}</>;
  }

  // If project-specific permission check
  if (projectId !== undefined) {
    if (!hasProjectAccess(projectId)) {
      return <>{fallback}</>;
    }
    
    const projectPermissions = getUserProjectPermissions(projectId);
    const permissions = permission.split(',').map(p => p.trim());
    
    const hasRequiredPermissions = requireAll
      ? permissions.every(p => projectPermissions.includes(p))
      : permissions.some(p => projectPermissions.includes(p));
    
    return hasRequiredPermissions ? <>{children}</> : <>{fallback}</>;
  }

  // Global permission check
  const permissions = permission.split(',').map(p => p.trim());
  const hasRequiredPermissions = requireAll
    ? permissions.every(p => hasPermission(p))
    : permissions.some(p => hasPermission(p));

  return hasRequiredPermissions ? <>{children}</> : <>{fallback}</>;
};

interface ConditionalRenderProps {
  condition: boolean;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const ConditionalRender: React.FC<ConditionalRenderProps> = ({
  condition,
  children,
  fallback = null
}) => {
  return condition ? <>{children}</> : <>{fallback}</>;
};

// Hook for permission checking in components
export const usePermissions = () => {
  const { hasPermission, hasProjectAccess, getUserProjectPermissions, user } = useAuth();

  const canAccess = (permission: string, projectId?: number): boolean => {
    if (projectId !== undefined) {
      if (!hasProjectAccess(projectId)) return false;
      const projectPermissions = getUserProjectPermissions(projectId);
      return projectPermissions.includes(permission);
    }
    return hasPermission(permission);
  };

  const canAccessAny = (permissions: string[], projectId?: number): boolean => {
    return permissions.some(permission => canAccess(permission, projectId));
  };

  const canAccessAll = (permissions: string[], projectId?: number): boolean => {
    return permissions.every(permission => canAccess(permission, projectId));
  };

  const getUserProjects = () => {
    return user?.userProjects || [];
  };

  const getAccessibleProjectIds = (): number[] => {
    return user?.userProjects?.map(p => p.projectId) || [];
  };

  return {
    canAccess,
    canAccessAny,
    canAccessAll,
    hasPermission,
    hasProjectAccess,
    getUserProjectPermissions,
    getUserProjects,
    getAccessibleProjectIds
  };
};

export default PermissionGuard;
